import { format } from "date-fns";
import { Campaign, CampaignRecipient } from "../../features/campaign/campaignTypes";

export function parseTriggerDate(dateStr: string): Date | null {
  if (!dateStr) return null;

  if (dateStr.includes("T")) {
    const parsed = new Date(dateStr);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  if (dateStr.includes("-")) {
    const [year, month, day] = dateStr.split("-").map(Number);
    if (year < 1900 || year > 2100) return null;
    return new Date(year, month - 1, day);
  }

  const parsed = new Date(dateStr);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function getActiveRecipients(campaign: Campaign): CampaignRecipient[] {
  return (campaign.recipient_list || []).filter((recipient) => !recipient.is_delete);
}

export type EventDateParts = {
  year: number;
  month: number;
  day: number;
};

export type ScheduledRecipientRow = {
  recipient: CampaignRecipient;
  campaignId: string;
  campaignName: string;
};

export function parseEventDateParts(dateStr?: string | null): EventDateParts | null {
  if (!dateStr) return null;

  const match = String(dateStr).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    return {
      year: Number(match[1]),
      month: Number(match[2]) - 1,
      day: Number(match[3]),
    };
  }

  const date = parseTriggerDate(dateStr);
  if (!date) return null;

  return {
    year: date.getFullYear(),
    month: date.getMonth(),
    day: date.getDate(),
  };
}

export function formatEventDateMd(dateStr?: string | null): string {
  const parts = parseEventDateParts(dateStr);
  if (!parts) return "—";
  return `${String(parts.month + 1).padStart(2, "0")}/${String(parts.day).padStart(2, "0")}`;
}

/** Recipients whose event_date falls in this calendar month, any year. */
export function isRecipientScheduledInMonth(
  recipient: CampaignRecipient,
  _year: number,
  month: number
): boolean {
  if (recipient.is_delete) return false;

  const parts = parseEventDateParts(recipient.event_date);
  if (!parts) return false;

  return parts.month === month;
}

export function getScheduledInMonth(
  campaigns: Campaign[],
  month: number,
  options?: { activeOnly?: boolean }
): ScheduledRecipientRow[] {
  const rows: ScheduledRecipientRow[] = [];
  const activeOnly = options?.activeOnly !== false;

  for (const campaign of campaigns) {
    if (campaign.is_delete) continue;
    if (activeOnly && !campaign.is_active) continue;

    for (const recipient of getActiveRecipients(campaign)) {
      if (!isRecipientScheduledInMonth(recipient, 0, month)) continue;
      rows.push({
        recipient,
        campaignId: campaign.campaign_id,
        campaignName: campaign.campaign_name || "Untitled Campaign",
      });
    }
  }

  rows.sort((a, b) => {
    const dayA = parseEventDateParts(a.recipient.event_date)?.day ?? 99;
    const dayB = parseEventDateParts(b.recipient.event_date)?.day ?? 99;
    if (dayA !== dayB) return dayA - dayB;
    return (a.recipient.name || "").localeCompare(b.recipient.name || "");
  });

  return rows;
}

export function countScheduledInMonth(
  campaign: Campaign,
  _year: number,
  month: number
): number {
  if (!campaign.is_active) return 0;

  return getScheduledInMonth([campaign], month).length;
}

export function computeCampaignStats(campaigns: Campaign[]) {
  const now = new Date();
  const month = now.getMonth();

  const visibleCampaigns = campaigns.filter((campaign) => !campaign.is_delete);

  let totalRecipients = 0;
  for (const campaign of visibleCampaigns) {
    totalRecipients += getActiveRecipients(campaign).length;
  }

  return {
    totalCampaigns: visibleCampaigns.length,
    activeCampaigns: visibleCampaigns.filter((campaign) => campaign.is_active).length,
    scheduledThisMonth: getScheduledInMonth(visibleCampaigns, month).length,
    totalRecipients,
    monthLabel: format(now, "MMMM yyyy"),
  };
}

export function formatCampaignType(type: string) {
  switch (type) {
    case "automated_birthday":
      return "Automated Birthday";
    case "automated_home_anniversary":
      return "Home Anniversary";
    case "no_gift":
      return "No Gift";
    case "postcard":
      return "Postcard";
    case "gift":
      return "Gift";
    default:
      return type || "N/A";
  }
}

export function getCampaignTypeStyle(type: string) {
  switch (type) {
    case "gift":
      return {
        backgroundColor: "rgba(111, 66, 193, 0.1)",
        color: "#5a2d91",
        border: "1px solid rgba(111, 66, 193, 0.2)",
      };
    case "no_gift":
      return {
        backgroundColor: "rgba(255, 152, 0, 0.1)",
        color: "#f57c00",
        border: "1px solid rgba(255, 152, 0, 0.2)",
      };
    case "automated_birthday":
      return {
        backgroundColor: "rgba(13, 110, 253, 0.1)",
        color: "#0d6efd",
        border: "1px solid rgba(13, 110, 253, 0.2)",
      };
    case "automated_home_anniversary":
      return {
        backgroundColor: "rgba(184, 137, 74, 0.12)",
        color: "#8f6a35",
        border: "1px solid rgba(184, 137, 74, 0.25)",
      };
    default:
      return {
        backgroundColor: "rgba(108, 117, 125, 0.1)",
        color: "#495057",
        border: "1px solid rgba(108, 117, 125, 0.2)",
      };
  }
}
